
import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchExecution } from '../slices/executionSlice';
import {
  HiOutlineArrowLeft,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineClock,
  HiOutlineCode,
} from 'react-icons/hi';

function StepCard({ step, index }) {
  const success = step.status === 'success';

  return (
    <div className={`card overflow-hidden border-l-4 ${
      success ? 'border-l-emerald-500' : 'border-l-red-500'
    }`}>
      <div className="flex items-center gap-3 px-5 py-3.5 bg-gray-50 dark:bg-gray-800/60">
        <span className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-bold">
          {index + 1}
        </span>

        {success
          ? <HiOutlineCheckCircle className="text-emerald-500" size={18} />
          : <HiOutlineXCircle className="text-red-500" size={18} />
        }

        <span className="font-semibold text-sm capitalize">
          {step.nodeType?.replace(/([A-Z])/g, ' $1').trim()}
        </span>

        <code className="ml-auto text-xs text-gray-400">
          {step.nodeId}
        </code>
      </div>

      <div className="p-4 space-y-4">
        {step.input && (
          <Section title="Input" data={step.input} />
        )}
        {step.output && (
          <Section title="Output" data={step.output} success />
        )}
        {step.error && (
          <Section title="Error" data={step.error} error />
        )}
      </div>
    </div>
  );
}

function Section({ title, data, success, error }) {
  return (
    <div>
      <p className={`text-xs font-semibold mb-2 ${
        error ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'
      }`}>
        {title}
      </p>

      <pre className={`rounded-xl p-4 text-xs font-mono overflow-x-auto ${
        error
          ? 'bg-red-50 dark:bg-red-900/20 text-red-600'
          : success
          ? 'bg-emerald-50 dark:bg-emerald-900/10 text-emerald-700'
          : 'bg-gray-50 dark:bg-gray-900 text-gray-700'
      }`}>
        {typeof data === 'string'
          ? data
          : JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

export default function ExecutionDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const { selected, loading } = useSelector((s) => s.executions || {});

  useEffect(() => {
    dispatch(fetchExecution(id));
  }, [id, dispatch]);

  if (!selected || loading || selected._id !== id) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const successSteps =
    selected.steps?.filter((s) => s.status === 'success').length || 0;

  return (
    <div className="animate-fade-in space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Link to="/executions" className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
          <HiOutlineArrowLeft size={20} />
        </Link>

        <div>
          <h1 className="text-2xl font-bold">Execution Detail</h1>
          <p className="text-sm text-gray-500 mt-1">
            {new Date(selected.createdAt).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Summary label="Status" value={selected.status} />
        <Summary label="Steps" value={`${successSteps}/${selected.steps?.length || 0}`} />
        <Summary label="Started" value={new Date(selected.createdAt).toLocaleTimeString()} />
        <Summary label="Scenario" value={selected.scenarioId?.name || '—'} />
      </div>

      {/* Trigger */}
      <div className="card p-5">
        <h3 className="flex items-center gap-2 font-semibold text-sm mb-3">
          <HiOutlineCode size={16} /> Trigger Payload
        </h3>
        <pre className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 text-xs font-mono overflow-x-auto">
          {JSON.stringify(selected.triggerData, null, 2)}
        </pre>
      </div>

      {/* Steps */}
      <div className="space-y-4">
        <h3 className="font-semibold text-sm">
          Execution Steps ({selected.steps?.length || 0})
        </h3>

        {selected.steps?.length ? (
          selected.steps.map((step, i) => (
            <StepCard key={i} step={step} index={i} />
          ))
        ) : (
          <p className="text-sm text-gray-400 card p-6 text-center">
            No steps recorded.
          </p>
        )}
      </div>
    </div>
  );
}

function Summary({ label, value }) {
  return (
    <div className="card p-4">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="font-semibold text-sm capitalize truncate">
        {value}
      </p>
    </div>
  );
}

